import { useMemo, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Container,
  Group,
  Pagination,
  Paper,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Search } from 'lucide-react';
import PageHeader from '@components/shared/PageHeader';
import {
  fetchLegacyV0Users,
  generateAllLegacyV0MigrationExcelData,
  generateAllLegacyV0MigrationJsonData,
  resetMigratedLegacyV0Users,
  updateLegacyV0UserMigrationStatus,
  type V0LegacyUserListItem,
} from '../services/legacyV0UserManager';
import { TableLoadingSkeleton } from './components/TableLoadingSkeleton';

const PAGE_SIZE = 25;

export default function AdminLegacyV0Users() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [migrationStatus, setMigrationStatus] = useState<
    'all' | 'pending' | 'ignored'
  >('pending');
  const [emailFilter, setEmailFilter] = useState<'all' | 'ok' | 'missing'>('all');
  const [generatingJson, setGeneratingJson] = useState(false);
  const [generatingExcel, setGeneratingExcel] = useState(false);
  const [resettingMigrated, setResettingMigrated] = useState(false);
  const [batchProgress, setBatchProgress] = useState<string | null>(null);
  const [savingUserId, setSavingUserId] = useState<number | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'admin',
      'legacy-v0-users',
      page,
      PAGE_SIZE,
      debouncedSearch,
      migrationStatus,
      emailFilter,
    ],
    queryFn: () =>
      fetchLegacyV0Users(
        page,
        PAGE_SIZE,
        debouncedSearch || undefined,
        migrationStatus,
        emailFilter
      ),
    placeholderData: (previous) => previous,
  });

  const users = data?.users ?? [];
  const totalPages = data?.pagination.totalPages ?? 1;
  const importMeta = data?.import_meta;

  const handleIgnoreToggle = async (
    event: MouseEvent,
    user: V0LegacyUserListItem
  ) => {
    event.stopPropagation();
    const current = user.migration?.status ?? 'pending';
    const next = current === 'ignored' ? 'pending' : 'ignored';
    setSavingUserId(user.id);
    try {
      await updateLegacyV0UserMigrationStatus(user.id, next);
      notifications.show({
        color: 'green',
        message: next === 'ignored' ? `Ignored #${user.id}` : `Re-opened #${user.id}`,
      });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'legacy-v0-users'] });
      await refetch();
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Status update failed',
      });
    } finally {
      setSavingUserId(null);
    }
  };

  const handleResetMigrated = async () => {
    if (
      !window.confirm(
        'Reset all migrated v0 contacts to pending?\nbe2025 data is kept — only the migration status returns to the starting state.'
      )
    ) {
      return;
    }
    setResettingMigrated(true);
    try {
      const result = await resetMigratedLegacyV0Users();
      notifications.show({
        color: 'green',
        message:
          result.reset === 0
            ? 'No migrated v0 contacts to reset'
            : `Reset ${result.reset} migrated contact(s) → pending`,
      });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'legacy-v0-users'] });
      await refetch();
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Reset migrated failed',
      });
    } finally {
      setResettingMigrated(false);
    }
  };

  const handleGenerateAllJson = async () => {
    const filterSummary = `${migrationStatus} · email:${emailFilter}`;
    if (
      !window.confirm(
        `Generate v0 migration JSON for filtered users (${filterSummary}) into migration/v0/?`
      )
    ) {
      return;
    }
    setGeneratingJson(true);
    setBatchProgress(`Listing JSON (${filterSummary})…`);
    try {
      const result = await generateAllLegacyV0MigrationJsonData({
        migrationStatus,
        email: emailFilter,
        onProgress: ({ done, total, userId, ok, path, error }) => {
          setBatchProgress(
            ok
              ? `${done}/${total} · #${userId} → ${path}`
              : `${done}/${total} · #${userId} FAILED: ${error}`
          );
        },
      });
      notifications.show({
        color: result.failed.length === 0 ? 'green' : 'orange',
        message: `Saved ${result.saved}/${result.total} JSON files (${filterSummary})`,
      });
      setBatchProgress(null);
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Generate all JSON failed',
      });
      setBatchProgress(null);
    } finally {
      setGeneratingJson(false);
    }
  };

  const handleGenerateAllExcel = async () => {
    const filterSummary = `${migrationStatus} · email:${emailFilter}`;
    if (
      !window.confirm(
        `Download one Excel with all filtered v0 contacts (${filterSummary})?`
      )
    ) {
      return;
    }
    setGeneratingExcel(true);
    setBatchProgress(`Collecting contacts (${filterSummary})…`);
    try {
      const result = await generateAllLegacyV0MigrationExcelData({
        migrationStatus,
        email: emailFilter,
        onProgress: ({ done, total, userId, ok, filename, error }) => {
          if (filename) {
            setBatchProgress(`Writing ${filename}…`);
            return;
          }
          setBatchProgress(
            ok
              ? `${done}/${total} · #${userId}`
              : `${done}/${total} · #${userId} FAILED: ${error}`
          );
        },
      });
      notifications.show({
        color: result.failed.length === 0 ? 'green' : 'orange',
        message: result.filename
          ? `Downloaded ${result.filename} (${result.total - result.failed.length}/${result.total} contacts)`
          : `No Excel generated (${result.failed.length} failed)`,
      });
      setBatchProgress(null);
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Export Excel failed',
      });
      setBatchProgress(null);
    } finally {
      setGeneratingExcel(false);
    }
  };

  const rows = useMemo(
    () =>
      users.map((user) => {
        const status = user.migration?.status ?? 'pending';
        return (
          <Table.Tr
            key={user.id}
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/admin/legacy-v0-users/${user.id}`)}
          >
            <Table.Td>{user.post_id}</Table.Td>
            <Table.Td>
              <Text size='sm' fw={600}>
                {user.display_name}
              </Text>
              <Text size='xs' c='dimmed'>
                {user.tipo_cliente || '—'}
              </Text>
            </Table.Td>
            <Table.Td>
              {user.email_ok ? (
                <Text size='sm'>{user.email}</Text>
              ) : (
                <Stack gap={2}>
                  <Badge color='red' variant='light' w='fit-content'>
                    no email
                  </Badge>
                  {user.email ? (
                    <Text size='xs' c='dimmed' style={{ wordBreak: 'break-all' }}>
                      fallback: {user.email}
                    </Text>
                  ) : null}
                </Stack>
              )}
            </Table.Td>
            <Table.Td>
              <Badge
                color={
                  status === 'ignored'
                    ? 'gray'
                    : status === 'migrated'
                      ? 'teal'
                      : 'blue'
                }
                variant='light'
              >
                {status}
              </Badge>
            </Table.Td>
            <Table.Td ta='right'>{user.abbonamenti_count || '—'}</Table.Td>
            <Table.Td ta='right'>{user.oculus_count || '—'}</Table.Td>
            <Table.Td>
              <Button
                size='xs'
                variant='light'
                loading={savingUserId === user.id}
                onClick={(event) => handleIgnoreToggle(event, user)}
              >
                {status === 'ignored' ? 'Undo ignore' : 'Ignore'}
              </Button>
            </Table.Td>
          </Table.Tr>
        );
      }),
    [users, navigate, savingUserId]
  );

  return (
    <Container size='100%' py='lg' px='md'>
      <Stack gap='lg'>
        <PageHeader
          title='Legacy Users (v0 WordPress)'
          subtitle='Static dump wpb_clienti + wpb_users. Email only when utente_wp joins wp_users.'
          breadcrumbs={[
            { label: 'Admin', href: '/admin/users' },
            { label: 'Legacy Migration', href: '/admin/legacy-migration' },
            { label: 'Legacy v0 users' },
          ]}
          showBackButton
          backUrl='/admin/legacy-migration'
          actions={
            <Group>
              <Button
                color='orange'
                variant='light'
                loading={resettingMigrated}
                disabled={generatingJson || generatingExcel}
                onClick={() => void handleResetMigrated()}
              >
                Reset migrated
              </Button>
              <Button
                color='grape'
                variant='light'
                loading={generatingJson}
                disabled={generatingExcel || resettingMigrated}
                onClick={handleGenerateAllJson}
              >
                Generate all JSON data
              </Button>
              <Button
                color='teal'
                variant='light'
                loading={generatingExcel}
                disabled={generatingJson || resettingMigrated}
                onClick={handleGenerateAllExcel}
              >
                Export Excel
              </Button>
              <Badge color='grape' variant='filled'>
                v0 wordpress
              </Badge>
            </Group>
          }
        />

        {batchProgress ? (
          <Text size='sm' c='dimmed'>
            {batchProgress}
          </Text>
        ) : null}

        {importMeta ? (
          <Text size='sm' c='dimmed'>
            Import: email_ok {String(importMeta.email_ok)}/{String(importMeta.clienti)} ·
            abbonamenti {String(importMeta.abbonamenti)} · oculus{' '}
            {String(importMeta.oculus)}
          </Text>
        ) : null}

        <Paper withBorder p='md' radius='md'>
          <Group justify='space-between' mb='md' wrap='wrap'>
            <TextInput
              placeholder='Search name, email, post_id'
              leftSection={<Search size={16} />}
              value={search}
              onChange={(event) => {
                setSearch(event.currentTarget.value);
                setPage(1);
              }}
              style={{ flex: 1, minWidth: 240 }}
            />
            <SegmentedControl
              value={migrationStatus}
              onChange={(value) => {
                setMigrationStatus(value as 'all' | 'pending' | 'ignored');
                setPage(1);
              }}
              data={[
                { label: 'All', value: 'all' },
                { label: 'To migrate', value: 'pending' },
                { label: 'Ignored', value: 'ignored' },
              ]}
            />
            <SegmentedControl
              value={emailFilter}
              onChange={(value) => {
                setEmailFilter(value as 'all' | 'ok' | 'missing');
                setPage(1);
              }}
              data={[
                { label: 'Email: all', value: 'all' },
                { label: 'With email', value: 'ok' },
                { label: 'No email', value: 'missing' },
              ]}
            />
          </Group>

          {isLoading ? (
            <TableLoadingSkeleton rows={8} columns={7} />
          ) : error ? (
            <Text c='red'>{(error as Error).message}</Text>
          ) : (
            <>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>post_id</Table.Th>
                    <Table.Th>Cliente</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th ta='right'>Abb.</Table.Th>
                    <Table.Th ta='right'>Oculus</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{rows}</Table.Tbody>
              </Table>
              <Group justify='space-between' mt='md'>
                <Text size='sm' c='dimmed'>
                  {data?.pagination.totalRecords ?? 0} users
                </Text>
                <Pagination value={page} onChange={setPage} total={totalPages} />
              </Group>
            </>
          )}
        </Paper>
      </Stack>
    </Container>
  );
}
